import { DrawerContextProvider, DrawerMode } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useQueryParams } from '../../../hooks/useQueryParams'

interface ImportedInvoiceDrawerParams {
  importedInvoiceId?: string
}

interface ContextState {
  closeDrawer: () => void
  importedInvoiceId?: string
  openDrawer: (importedInvoiceId: string) => void
}

const ImportedInvoiceDrawerContext = createContext<ContextState | undefined>(undefined)

interface ImportedInvoiceDrawerProviderProps {
  children?: ReactNode
}

/**
 * Makes `importedInvoiceId` the single source of truth for the review drawer: the DS provider renders the
 * mode derived from the URL, and the only way back out is `closeDrawer`, which drops the param.
 * Shadows the app-level provider (which serves the help-center chat drawer) for this subtree only.
 *
 * Owns its own `useQueryParams` instance, so it reads and writes only `importedInvoiceId` and leaves
 * any other params in the URL (e.g. the list filters) untouched.
 *
 * No `onDrawerModeChange`: `expandable` is false on this drawer, nothing under `importedInvoices`
 * calls `useDrawer()`, and every close path (header button, click-away, Escape) goes through
 * `requestClose` — which `onCloseRequest` intercepts. So no internal transition can desync the URL.
 */
export const ImportedInvoiceDrawerProvider = ({ children }: ImportedInvoiceDrawerProviderProps): ReactElement => {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const [{ importedInvoiceId }, setDrawerParams] = useQueryParams<ImportedInvoiceDrawerParams>({
    enabled: true,
    // No real defaults to seed, so the mount effect would only write the search back unchanged.
    withDefaultQueryInUrl: false,
    defaultQuery: { importedInvoiceId: undefined },
  })

  // The drawer covers the whole content area, so Back has to close it rather than leave the view.
  // `setDrawerParams` always replaces, so opening first pushes the current URL a second time and lets
  // the write land on that copy: Back then returns to the view exactly as it was. Every other
  // transition replaces, which keeps the drawer out of history apart from the one entry Back needs.
  const openDrawer = useCallback(
    (nextImportedInvoiceId: string) => {
      navigate({ pathname, search })
      setDrawerParams({ importedInvoiceId: nextImportedInvoiceId })
    },
    [navigate, pathname, search, setDrawerParams],
  )

  // `qs.stringify` drops `undefined`, so this removes the param from the URL.
  const closeDrawer = useCallback(() => {
    setDrawerParams({ importedInvoiceId: undefined })
  }, [setDrawerParams])

  const value = useMemo(
    (): ContextState => ({ closeDrawer, importedInvoiceId, openDrawer }),
    [closeDrawer, importedInvoiceId, openDrawer],
  )

  return (
    <ImportedInvoiceDrawerContext.Provider value={value}>
      <DrawerContextProvider
        drawerMode={importedInvoiceId ? DrawerMode.Expanded : DrawerMode.Closed}
        onCloseRequest={closeDrawer}
      >
        {children}
      </DrawerContextProvider>
    </ImportedInvoiceDrawerContext.Provider>
  )
}

export const useImportedInvoiceDrawer = (): ContextState => {
  const context = useContext(ImportedInvoiceDrawerContext)

  if (!context) {
    throw new Error('ImportedInvoiceDrawerContext is missing in the module!')
  }

  return context
}
